import type { components } from "@/types/api.gen"

/*
   The review statuses the queue's Status filter offers: the backend's own
   `ReviewStatus`, labelled the way the legacy queue labels them. A value is
   sent to `GET /threads?status=` verbatim.

   These are not the four tiles. A tile groups several statuses (Queued is
   pending + running; In review is the remainder), so the filter and the
   tiles answer different questions and are kept apart on purpose.
*/

export type ReviewStatus = components["schemas"]["ReviewStatus"]

/** Typed as a full Record so a status the backend adds fails to compile here. */
export const REVIEW_STATUS_LABEL: Record<ReviewStatus, string> = {
  pending: "Queued",
  running: "Running",
  completed: "In review",
  needs_input: "Needs an answer",
  cancelled: "Stopped",
  needs_sme: "Needs SME",
  verified: "Cleared",
  failed: "Failed",
}

/** The legacy queue's dropdown order. */
export const REVIEW_STATUS_ORDER: readonly ReviewStatus[] = [
  "pending",
  "running",
  "completed",
  "needs_input",
  "cancelled",
  "needs_sme",
  "verified",
  "failed",
]

export function isReviewStatus(value: unknown): value is ReviewStatus {
  return typeof value === "string" && Object.hasOwn(REVIEW_STATUS_LABEL, value)
}

/**
 * A version's status as `/threads` sends it. The backend types `VersionSummary.status`
 * as a plain `str`, so a status it adds (`initiated`, on several branches) arrives here
 * before this file knows it. Kept as the string it is rather than cast to `ReviewStatus`.
 */
export type WireReviewStatus = string

/**
 * What a row's status pill reads. A status the queue does not know yet is shown as
 * itself, made readable ("initiated" → "Initiated"), rather than as a blank pill.
 */
export function reviewStatusLabel(status: WireReviewStatus): string {
  if (isReviewStatus(status)) return REVIEW_STATUS_LABEL[status]
  const words = status.replaceAll("_", " ").trim()
  if (!words) return "Unknown"
  return words.charAt(0).toUpperCase() + words.slice(1)
}
